import type { AxiosInstance } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import type { ApiDataList, ApiMakeItem, ApiModelItem, CatalogEntry, ApiCatalogFacetMake } from 'src/types/common';

import { useQuery, queryOptions } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { READ_TIMEOUT_MS } from 'src/utils/requestError';

import { QueryKeys } from './QueryKeys';

/**
 * A make's or a model's id, name and slug change only with a release of the catalogue: once the
 * api has answered a lookup, the answer stands for the visit — never stale, never dropped.
 */
const FOR_THE_VISIT = { staleTime: Infinity, gcTime: Infinity } as const;

/**
 * The entry an answer holds under exactly this slug, or `null` — the catalogue has no such name.
 * The slug is read off the answer, not assumed from the question: an api that does not know the
 * `slug` parameter answers its whole list, and the first entry of that is not the one asked for.
 */
const entryUnder = <T extends { id: string; name: string; slug?: string }>(
  items: T[] | undefined,
  slug: string,
  fits: (item: T) => boolean = () => true
): CatalogEntry | null => {
  // An answer that is not a list at all says nothing about the catalogue: a refusal, to be asked
  // again — not "no such name", which would stand for the whole visit.
  if (!Array.isArray(items)) throw new Error('the catalogue answered without its entries');

  const found = items.find((item) => item.slug === slug && fits(item));

  return found ? { id: found.id, name: found.name, slug } : null;
};

/**
 * The make lookup as a query: `GET /common/makes?slug=`, one entry or none. Asked by the hook
 * below and by the server's render of an address that names a make (`src/server/catalog.server.ts`).
 */
export const catalogMakeQuery = (slug: string, http: Pick<AxiosInstance, 'get'> = axios) =>
  queryOptions({
    queryKey: QueryKeys.catalogMake(slug),
    queryFn: async (): Promise<CatalogEntry | null> => {
      const response = await http.get<ApiDataList<ApiMakeItem>>('/common/makes', {
        params: { slug },
        // A lookup that never answers becomes "taking longer", like the feed it stands in front of.
        timeout: READ_TIMEOUT_MS,
      });

      return entryUnder(response.data?.items, slug);
    },
    ...FOR_THE_VISIT,
  });

/** The model lookup as a query: `GET /common/models` with the make's id and the slug. */
export const catalogModelQuery = (makeId: string, slug: string, http: Pick<AxiosInstance, 'get'> = axios) =>
  queryOptions({
    queryKey: QueryKeys.catalogModel(makeId, slug),
    queryFn: async (): Promise<CatalogEntry | null> => {
      const response = await http.get<ApiDataList<ApiModelItem>>('/common/models', {
        params: { makeId, slug },
        timeout: READ_TIMEOUT_MS,
      });

      return entryUnder(response.data?.items, slug, (item) => item.makeId === makeId);
    },
    ...FOR_THE_VISIT,
  });

/**
 * The make an address names (`/buy/<slug>`), asked of the catalogue by its slug —
 * `GET /common/makes?slug=`, one entry or none. The slug is the api's own field (batch 2b): the
 * storefront no longer downloads the whole reference list to compute slugs of its own.
 *
 * `data` is the make, or `null` once the api has said there is none. No slug, no request. A make
 * the filter panel has offered is here already — the facets put it in (`seedCatalogEntries`) — so
 * the address that names it next resolves without a request.
 */
export function useCatalogMake(slug: string | undefined) {
  return useQuery({ ...catalogMakeQuery(slug ?? ''), enabled: !!slug });
}

/**
 * The model an address names under its make (`/buy/<make>/<slug>`): `GET /common/models` with the
 * make's id and the slug. A slug is unique only within its make, so there is no request without one.
 */
export function useCatalogModel(makeId: string | undefined, slug: string | undefined) {
  return useQuery({ ...catalogModelQuery(makeId ?? '', slug ?? ''), enabled: !!makeId && !!slug });
}

const entryOf = ({ id, name, slug }: CatalogEntry): CatalogEntry => ({ id, name, slug });

/**
 * Hands the makes and models a facets answer names to the lookups above: the panel has just shown
 * the person these entries, so the address that names one next has nothing to ask.
 *
 * What is put in has no reader yet — the address that names it comes later, perhaps much later —
 * and a query nobody reads is dropped after five minutes by default. So both kinds of lookup are
 * told to keep theirs for the visit, here as well as in the hooks: a make picked from a list six
 * minutes old must not wait for a lookup the list already answered.
 */
export const seedCatalogEntries = (client: QueryClient, makes: ApiCatalogFacetMake[]): void => {
  client.setQueryDefaults(QueryKeys.catalogMakes(), FOR_THE_VISIT);
  client.setQueryDefaults(QueryKeys.catalogModels(), FOR_THE_VISIT);

  for (const make of makes) {
    client.setQueryData(QueryKeys.catalogMake(make.slug), entryOf(make));
    for (const model of make.models) {
      client.setQueryData(QueryKeys.catalogModel(make.id, model.slug), entryOf(model));
    }
  }
};
